import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import { teaPurchaseKeys } from './use-tea-purchase-query'
import type {
    CreateDriedTeaPurchaseRequest,
    ListDriedTeaPurchasesParams,
    PatchDriedTeaPurchaseRequest,
} from '@/lib/api/dried-tea-purchase-api'
import { driedTeaPurchaseApi } from '@/lib/api/dried-tea-purchase-api'
import { downloadBlob } from '@/lib/download-blob'
import { m } from '@/lib/paraglide/messages'

export const driedTeaPurchaseKeys = {
    all: ['dried-tea-purchases'] as const,
    list: (params: ListDriedTeaPurchasesParams | undefined) =>
        [...driedTeaPurchaseKeys.all, 'list', params ?? {}] as const,
    detail: (id: string) =>
        [...driedTeaPurchaseKeys.all, 'detail', id] as const,
    infinite: (params: Omit<ListDriedTeaPurchasesParams, 'page'>) =>
        [...driedTeaPurchaseKeys.all, 'infinite', params] as const,
}

/**
 * Phiếu cân khô theo trang, tải thêm khi cuộn — picker nguồn của phiếu nhập
 * kho lô phân loại (search phía server).
 */
export function useDriedTeaPurchasesInfinite(
    params: Omit<ListDriedTeaPurchasesParams, 'page' | 'per_page'>,
) {
    return useInfiniteQuery({
        queryKey: driedTeaPurchaseKeys.infinite(params),
        queryFn: async ({ pageParam }) => {
            const res = await driedTeaPurchaseApi.list({
                ...params,
                page: pageParam,
                per_page: 50,
            })
            return { items: res.data ?? [], meta: res.meta }
        },
        initialPageParam: 1,
        placeholderData: (prev) => prev,
        getNextPageParam: (lastPage) => {
            if (!lastPage.meta) return undefined
            return lastPage.meta.page < lastPage.meta.total_pages
                ? lastPage.meta.page + 1
                : undefined
        },
    })
}

export function useDriedTeaPurchasesList(
    params?: ListDriedTeaPurchasesParams,
    opts?: { enabled?: boolean },
) {
    return useQuery({
        queryKey: driedTeaPurchaseKeys.list(params),
        enabled: opts?.enabled ?? true,
        queryFn: async () => {
            const res = await driedTeaPurchaseApi.list(params)
            return res
        },
    })
}

export function useDriedTeaPurchase(id: string | undefined) {
    return useQuery({
        queryKey: id
            ? driedTeaPurchaseKeys.detail(id)
            : ['dried-tea-purchases', 'detail', 'none'],
        enabled: !!id,
        queryFn: async () => {
            const res = await driedTeaPurchaseApi.get(id ?? '')
            return res.data
        },
    })
}

// Mutation phiếu khô invalidate CẢ query phiếu tươi: gộp/bỏ gộp đổi trạng thái
// "Đã gộp phiếu khô" (badge merged) trên màn tươi.
function invalidateBoth(qc: ReturnType<typeof useQueryClient>) {
    void qc.invalidateQueries({ queryKey: driedTeaPurchaseKeys.all })
    void qc.invalidateQueries({ queryKey: teaPurchaseKeys.all })
}

/**
 * Tạo nhiều phiếu khô. Không tự toast lỗi: dialog gắn lỗi của từng dòng vào
 * đúng ô (details.row), chỉ lỗi chung mới hiện toast ở dialog.
 */
export function useBulkCreateDriedTeaPurchases() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (items: CreateDriedTeaPurchaseRequest[]) =>
            driedTeaPurchaseApi.bulkCreate(items),
        onSuccess: (res) => {
            toast.success(
                m.dtp_bulk_success({ count: String(res.data?.length ?? 0) }),
            )
            invalidateBoth(qc)
        },
    })
}

export function useCreateDriedTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateDriedTeaPurchaseRequest) =>
            driedTeaPurchaseApi.create(data),
        onSuccess: () => {
            toast.success(m.dtp_create_success())
            invalidateBoth(qc)
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.dtp_create_error())
        },
    })
}

export function usePatchDriedTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: PatchDriedTeaPurchaseRequest
        }) => driedTeaPurchaseApi.patch(id, data),
        onSuccess: () => {
            toast.success(m.dtp_update_success())
            invalidateBoth(qc)
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.dtp_update_error())
        },
    })
}

/**
 * Xoá phiếu cân khô nhập sai. Xoá xong các phiếu tươi nguồn hết "đã gộp" nên
 * invalidate cả hai danh sách. Lỗi chặn (409) hiển thị nguyên văn từ BE.
 */
export function useDeleteDriedTeaPurchase() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => driedTeaPurchaseApi.delete(id),
        onSuccess: () => {
            toast.success(m.dtp_delete_success())
            invalidateBoth(qc)
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.dtp_delete_error())
        },
    })
}

/**
 * Đánh dấu hộ đã trả tờ phiếu cân giấy của phiếu khô (cờ một chiều). Chỉ đổi
 * phiếu khô nên chỉ invalidate danh sách khô.
 */
export function useMarkDriedBallotReturned() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => driedTeaPurchaseApi.markBallotReturned(id),
        onSuccess: () => {
            toast.success(m.tea_purchase_ballot_success())
            void qc.invalidateQueries({ queryKey: driedTeaPurchaseKeys.all })
        },
        onError: (err: unknown) => {
            const msg = (err as { message?: string })?.message
            toast.error(msg || m.tea_purchase_ballot_error())
        },
    })
}

export function useExportDriedTeaPurchases() {
    return useMutation({
        mutationFn: async (params: ListDriedTeaPurchasesParams) => {
            const { blob, filename } =
                await driedTeaPurchaseApi.exportExcel(params)
            downloadBlob(blob, filename ?? 'CHE_THANH_PHAM.xlsx')
        },
        onError: () => {
            toast.error(m.tea_purchase_export_failed())
        },
    })
}
